import { TextInput, Textarea, Switch, Stack, Loader } from '@mantine/core';
import { useController, type Control, type FieldValues, type Path } from 'react-hook-form';
import { PLAYLIST_CONSTRAINTS } from '@/lib/constants/playlist';

/**
 * Generic settings form shared by SmartPlaylistContent and ManualPlaylistContent.
 *
 * Uses generic Control<T> because:
 * - SmartPlaylistContent uses Control<SmartPlaylistFormValues> (settings + tag content merged)
 * - ManualPlaylistContent uses Control<PlaylistSettingsFormValues> (settings only)
 * - Both contain the same 4 settings fields
 *
 * RHF's Control<T> is invariant, requiring Path<T> casts for field names.
 */
interface PlaylistSettingsFormProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  /** Uniqueness conflict message for the Reference ID field (from useRefIdConflict). */
  refIdConflictError?: string | null;
  refIdChecking?: boolean;
}

export function PlaylistSettingsForm<T extends FieldValues>({ control, refIdConflictError, refIdChecking }: PlaylistSettingsFormProps<T>) {
  const titleField = useController({ name: 'title' as Path<T>, control });
  const descField = useController({ name: 'descShort' as Path<T>, control });
  const refIdField = useController({ name: 'refId' as Path<T>, control });
  const stateField = useController({ name: 'mediaStateType' as Path<T>, control });

  const isActive = stateField.field.value === 'active';

  return (
    <Stack gap="md" maw={800}>
      <TextInput
        label="Title"
        required
        value={titleField.field.value}
        onChange={titleField.field.onChange}
        onBlur={titleField.field.onBlur}
        error={titleField.fieldState.error?.message}
        description={`${(titleField.field.value as string).length}/${PLAYLIST_CONSTRAINTS.TITLE_MAX}`}
        maxLength={PLAYLIST_CONSTRAINTS.TITLE_MAX}
      />

      <Textarea
        label="Short Description"
        value={descField.field.value}
        onChange={descField.field.onChange}
        onBlur={descField.field.onBlur}
        error={descField.fieldState.error?.message}
        description={`${(descField.field.value as string).length}/${PLAYLIST_CONSTRAINTS.DESC_SHORT_MAX}`}
        maxLength={PLAYLIST_CONSTRAINTS.DESC_SHORT_MAX}
        rows={3}
      />

      <TextInput
        label="Reference ID"
        value={refIdField.field.value}
        onChange={refIdField.field.onChange}
        onBlur={refIdField.field.onBlur}
        error={refIdField.fieldState.error?.message ?? refIdConflictError ?? undefined}
        description={`${(refIdField.field.value as string).length}/${PLAYLIST_CONSTRAINTS.REF_ID_MAX}`}
        maxLength={PLAYLIST_CONSTRAINTS.REF_ID_MAX}
        placeholder="Optional unique identifier"
        rightSection={refIdChecking ? <Loader size="xs" /> : undefined}
      />

      <Switch
        label={isActive ? 'Active — playlist is available' : 'Inactive — playlist is hidden'}
        checked={isActive}
        onChange={(e) => stateField.field.onChange(e.currentTarget.checked ? 'active' : 'inactive')}
        color={isActive ? 'green' : 'gray'}
      />
    </Stack>
  );
}
